/* CaseCard — list-item card used by MobileCases.
   Top row (single horizontal line):
     RIGHT side  → [ref-number badge] [case-type badge — coloured per category]
     LEFT side   → creation date + time (Iraqi calendar, 12-hour clock)
   Bottom action row:
     LEFT side   → [عرض] [حذف]
     RIGHT side  → probability gauge */
import { Eye, Trash } from "@phosphor-icons/react";
import ProbabilityGauge, { probBand } from "./ProbabilityGauge";
import { caseTypeColor, shortCaseType } from "../../lib/caseTypeColors";

const IRAQI_MONTHS = [
  "كانون الثاني", "شباط", "آذار", "نيسان", "أيار", "حزيران",
  "تموز", "آب", "أيلول", "تشرين الأول", "تشرين الثاني", "كانون الأول",
];

export const formatIraqDate = (s) => {
  const d = new Date(s);
  return `${d.getDate()} ${IRAQI_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export const formatIraqDateTime = (s) => {
  const d = new Date(s);
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const period = h >= 12 ? "م" : "ص";
  h = h % 12 || 12;
  return `${formatIraqDate(s)} · ${h}:${m} ${period}`;
};

export default function CaseCard({ c, onDelete, onView }) {
  const an = c.analysis || {};
  const prob = typeof an.success_probability === "number" ? an.success_probability : null;
  const band = probBand(prob);
  return (
    <article
      className="bg-white/60 backdrop-blur-md rounded-[20px] border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)] p-4"
      data-testid={`mobile-case-${c.id}`}
    >
      <div className="min-w-0 text-start">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-1.5 min-w-0">
            {c.ref_number && (
              <div
                className="inline-block px-2.5 py-1 text-[13px] font-bold text-[#B8860B] bg-white border border-[#D4AF37]/40 rounded-full whitespace-nowrap"
                dir="ltr"
              >
                {c.ref_number}
              </div>
            )}
            {an.case_type && (
              <div
                className="inline-block px-2.5 py-1 text-[13px] font-bold text-white rounded-full whitespace-nowrap shadow-sm"
                style={{ backgroundColor: caseTypeColor(an.case_type) }}
              >
                {shortCaseType(an.case_type)}
              </div>
            )}
          </div>
          <div className="text-[11px] text-[#5A6470] whitespace-nowrap shrink-0">
            {formatIraqDateTime(c.created_at)}
          </div>
        </div>
        <h3 className="font-tajawal text-[18px] font-bold text-[#0A192F] leading-snug">
          {c.title}
        </h3>
        <div className="text-[14px] text-[#3A4654] mt-1.5 leading-relaxed">
          {an.competent_court || "—"}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#0A192F]/10">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onView(c.id)}
            data-testid={`mobile-view-${c.id}`}
            className="px-4 py-1.5 text-[14px] font-bold text-[#0A192F] bg-white rounded-full border border-[#EAEAEA] flex items-center gap-1.5 active:scale-95 transition-transform"
          >
            <Eye size={15} weight="duotone" />
            <span>عرض</span>
          </button>
          <button
            onClick={() => onDelete(c.id)}
            data-testid={`mobile-delete-${c.id}`}
            className="px-4 py-1.5 text-[14px] font-bold text-[#8B0000] bg-white rounded-full border border-[#EAEAEA] flex items-center gap-1.5 active:scale-95 transition-transform"
          >
            <Trash size={15} weight="duotone" />
            <span>حذف</span>
          </button>
        </div>
        {band ? (
          <ProbabilityGauge value={prob} band={band} testid={`mobile-prob-${c.id}`} />
        ) : <span />}
      </div>
    </article>
  );
}
